import { Timestamp, UUID, ValueOf } from './common'
import {
  Channel,
  IncidentActionCompleteRequest,
  IncidentActionSkipRequest,
  Page,
  QueueData,
  QueueStatsFilterRequest,
  SpecialisationData,
  TicketsFilterType,
  TicketsRequestSortBy,
  TimeScale,
  VolumeAggregationDimension,
  VolumeAggregationDimensionTarget,
  VolumeComparisonType,
} from './generated'

export const VolumeAggregationDimensionExtended = {
  ...VolumeAggregationDimension,
  Outcomes: 'OUTCOME',
} as const

export type VolumeAggregationDimensionExtended = ValueOf<
  typeof VolumeAggregationDimensionExtended
>

export type HistoricalAnalyticsRequestParams = {
  queueId: UUID[]
  specialisationId: UUID[]
  departmentId: UUID[]
  from: Timestamp
  to: Timestamp
  scale: TimeScale
  areaId?: UUID[]
}

export type TicketsVolumeRequestParams = HistoricalAnalyticsRequestParams & {
  channel: string[]
  dimension: VolumeAggregationDimensionExtended
  dimensionTarget?: VolumeAggregationDimensionTarget
  comparisonType?: VolumeComparisonType
  filter?: QueueStatsFilterRequest
}

export type TicketOutcomesVolumeRequestParams = Omit<
  TicketsVolumeRequestParams,
  'channel' | 'dimension'
>

export type ChanneledRequestParams = {
  channel?: string
}

export type AgentAttendanceRequestParams = {
  channel?: string[]
  agentId?: UUID[]
  teamId?: UUID[]
  departmentId?: UUID[]
  areaId?: UUID[]
  specialisationId?: UUID[]
  queueId?: UUID[]
  from?: Timestamp
  to?: Timestamp
  start?: string
  end?: string
}

export type QueuesHealthDetailsRequestParams = {
  channel: string[]
  departmentId?: UUID[]
  areaId?: UUID[]
  specialisationId?: UUID[]
  queueId?: UUID[]
  filter?: QueueStatsFilterRequest
}

export const AgentsFilterType = {
  Scheduled: 'SCHEDULED',
  Online: 'ONLINE',
  Offline: 'OFFLINE',
  OnBreak: 'ON_BREAK',
  Absent: 'ABSENT',
  Overtime: 'OVERTIME',
  WrapUp: 'SHIFT_WRAP_UP',
} as const
export type AgentsFilterType = (typeof AgentsFilterType)[keyof typeof AgentsFilterType]
export const IncidentsFilterType = {
  Incidents: 'INCIDENTS',
} as const
export type IncidentsFilterType =
  (typeof IncidentsFilterType)[keyof typeof IncidentsFilterType]
export const ResourceFilterType = {
  ...TicketsFilterType,
  ...AgentsFilterType,
  ...IncidentsFilterType,
}
export type ResourceFilterType =
  (typeof ResourceFilterType)[keyof typeof ResourceFilterType]

export type ResourcesSideParams = {
  filterType: ResourceFilterType
} & (
  | {
      specialisation: SpecialisationDataWithoutSystem
      queue?: never
    }
  | {
      queue: QueueData
      specialisation?: never
    }
)

export type TicketsRequestParams = ResourcesSideParams & {
  channel?: string
  filter?: QueueStatsFilterRequest
  queueIds?: UUID[]
  sortBy?: TicketsRequestSortBy
  page?: Page
}

export type QueueStatsFilterRequestParams = { channel: Channel }

export type CompleteAlertingIncidentActionParams = {
  incidentActionId: UUID
  body: IncidentActionSkipRequest
}
export type SkipAlertingIncidentActionParams = {
  incidentActionId: UUID
  body: IncidentActionCompleteRequest
}

export type SpecialisationDataWithoutSystem = Omit<
  SpecialisationData,
  'queueingSystem' | 'departmentId' | 'departmentName' | 'areaId' | 'areaName'
>
